import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  BarChart3, 
  TrendingUp, 
  TrendingDown,
  Users, 
  AlertTriangle, 
  CheckCircle,
  Clock,
  Calendar,
  IndianRupee,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  Activity,
  Target,
  Flame
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdvancedDashboard = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [staffProductivity, setStaffProductivity] = useState(null);
  const [clientHealth, setClientHealth] = useState(null);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [dashboardRes, staffRes, healthRes] = await Promise.all([
        axios.get(`${API_URL}/api/analytics/dashboard`, { headers }),
        axios.get(`${API_URL}/api/analytics/staff-productivity`, { headers }),
        axios.get(`${API_URL}/api/analytics/client-health`, { headers })
      ]);

      setDashboardData(dashboardRes.data);
      setStaffProductivity(staffRes.data);
      setClientHealth(healthRes.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast({
        title: "Error",
        description: "Failed to load analytics data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const summary = dashboardData?.summary || {};
  const overdue = dashboardData?.overdue_summary || {};
  const receivables = dashboardData?.receivables_aging || {};

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Practice Intelligence Dashboard</h1>
          <p className="text-gray-500">Decision-oriented analytics for your practice</p>
        </div>
        <Button variant="outline" onClick={fetchAllData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Key Metrics Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Total Clients</p>
                <p className="text-2xl font-bold">{summary.total_clients || 0}</p>
              </div>
              <Users className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Pending Tasks</p>
                <p className="text-2xl font-bold text-orange-600">{summary.pending_tasks || 0}</p>
              </div>
              <Clock className="w-8 h-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Overdue Tasks</p>
                <p className="text-2xl font-bold text-red-600">{overdue.tasks?.total || 0}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Pending Invoices</p>
                <p className="text-2xl font-bold">{summary.pending_invoices || 0}</p>
              </div>
              <IndianRupee className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Receivables</p>
                <p className="text-2xl font-bold">₹{(summary.total_receivables || 0).toLocaleString()}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Team Size</p>
                <p className="text-2xl font-bold">{summary.total_staff || 0}</p>
              </div>
              <Activity className="w-8 h-8 text-indigo-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="compliance" className="space-y-4">
        <TabsList>
          <TabsTrigger value="compliance">
            <Calendar className="w-4 h-4 mr-2" />
            Compliance Heatmap
          </TabsTrigger>
          <TabsTrigger value="overdue">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Overdue Tracker
          </TabsTrigger>
          <TabsTrigger value="revenue">
            <TrendingUp className="w-4 h-4 mr-2" />
            Revenue Trends
          </TabsTrigger>
          <TabsTrigger value="receivables">
            <IndianRupee className="w-4 h-4 mr-2" />
            Receivables Aging
          </TabsTrigger>
          <TabsTrigger value="staff">
            <Users className="w-4 h-4 mr-2" />
            Staff Workload
          </TabsTrigger>
          <TabsTrigger value="health">
            <Target className="w-4 h-4 mr-2" />
            Client Health
          </TabsTrigger>
        </TabsList>

        {/* Compliance Heatmap */}
        <TabsContent value="compliance">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Flame className="w-5 h-5 text-orange-500" />
                Compliance Heatmap - Next 30 Days
              </CardTitle>
              <CardDescription>Tasks due by day - plan ahead to avoid compliance bottlenecks</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-7 gap-2">
                {dashboardData?.compliance_heatmap?.slice(0, 28).map((day, idx) => (
                  <div
                    key={idx}
                    className={`p-2 rounded text-center text-xs ${
                      day.count === 0 ? 'bg-gray-100' :
                      day.intensity === 'high' ? 'bg-red-500 text-white' :
                      day.intensity === 'medium' ? 'bg-orange-400 text-white' :
                      'bg-yellow-300'
                    }`}
                    title={`${day.date}: ${day.count} tasks`}
                  >
                    <div className="font-medium">{day.day}</div>
                    <div className="text-lg font-bold">{day.count}</div>
                  </div>
                ))}
              </div>
              <div className="flex gap-4 mt-4 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-gray-100 rounded"></div>
                  <span>No tasks</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-yellow-300 rounded"></div>
                  <span>1-2 tasks</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-orange-400 rounded"></div>
                  <span>3-5 tasks</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-red-500 rounded"></div>
                  <span>5+ tasks</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Overdue Tracker */}
        <TabsContent value="overdue">
          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-red-600">Overdue Tasks</CardTitle>
                <CardDescription>Tasks past their due date requiring immediate attention</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 mb-4">
                  <div className="flex justify-between items-center p-2 bg-red-50 rounded">
                    <span>Critical (7+ days)</span>
                    <Badge variant="destructive">{overdue.tasks?.critical || 0}</Badge>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-orange-50 rounded">
                    <span>Warning (4-7 days)</span>
                    <Badge className="bg-orange-500">{overdue.tasks?.warning || 0}</Badge>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-yellow-50 rounded">
                    <span>Minor (1-3 days)</span>
                    <Badge className="bg-yellow-500">{overdue.tasks?.minor || 0}</Badge>
                  </div>
                </div>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {overdue.tasks?.items?.map((task, idx) => (
                    <div key={idx} className="flex justify-between items-center p-2 border rounded text-sm">
                      <span className="truncate flex-1">{task.title}</span>
                      <Badge variant="destructive">{task.days_overdue}d</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-red-600">Overdue Invoices</CardTitle>
                <CardDescription>Unpaid invoices affecting cash flow</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center mb-4">
                  <p className="text-3xl font-bold text-red-600">₹{(overdue.invoices?.total_amount || 0).toLocaleString()}</p>
                  <p className="text-sm text-gray-500">Total Overdue Amount</p>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-2 bg-red-50 rounded">
                    <span>Critical (30+ days)</span>
                    <Badge variant="destructive">{overdue.invoices?.critical || 0}</Badge>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-orange-50 rounded">
                    <span>Warning (15-30 days)</span>
                    <Badge className="bg-orange-500">{overdue.invoices?.warning || 0}</Badge>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-yellow-50 rounded">
                    <span>Minor (1-15 days)</span>
                    <Badge className="bg-yellow-500">{overdue.invoices?.minor || 0}</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Revenue Trends */}
        <TabsContent value="revenue">
          <Card>
            <CardHeader>
              <CardTitle>Revenue Trends - Last 12 Months</CardTitle>
              <CardDescription>Compare invoiced vs collected amounts</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {dashboardData?.revenue_trends?.map((month, idx) => {
                  const maxValue = Math.max(...dashboardData.revenue_trends.map(m => Math.max(m.invoiced, m.collected))) || 1;
                  return (
                    <div key={idx} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="font-medium w-20">{month.month}</span>
                        <span className="text-blue-600">₹{month.invoiced.toLocaleString()}</span>
                        <span className="text-green-600">₹{month.collected.toLocaleString()}</span>
                      </div>
                      <div className="flex gap-1 h-4">
                        <div 
                          className="bg-blue-500 rounded-sm"
                          style={{ width: `${(month.invoiced / maxValue) * 50}%` }}
                          title={`Invoiced: ₹${month.invoiced}`}
                        ></div>
                        <div 
                          className="bg-green-500 rounded-sm"
                          style={{ width: `${(month.collected / maxValue) * 50}%` }}
                          title={`Collected: ₹${month.collected}`}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-4 mt-4 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-blue-500 rounded"></div>
                  <span>Invoiced</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-green-500 rounded"></div>
                  <span>Collected</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Receivables Aging */}
        <TabsContent value="receivables">
          <Card>
            <CardHeader>
              <CardTitle>Outstanding Receivables Aging</CardTitle>
              <CardDescription>Breakdown of outstanding amounts by age</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center mb-6">
                <p className="text-4xl font-bold">₹{(receivables.total || 0).toLocaleString()}</p>
                <p className="text-gray-500">Total Outstanding</p>
              </div>
              <div className="grid grid-cols-5 gap-4">
                {[
                  { label: 'Current', key: 'current', color: 'bg-green-500' },
                  { label: '1-30 Days', key: '1_30', color: 'bg-yellow-500' },
                  { label: '31-60 Days', key: '31_60', color: 'bg-orange-500' },
                  { label: '61-90 Days', key: '61_90', color: 'bg-red-400' },
                  { label: '90+ Days', key: 'over_90', color: 'bg-red-600' },
                ].map((bucket) => (
                  <div key={bucket.key} className="text-center">
                    <div className={`${bucket.color} text-white rounded-lg p-4 mb-2`}>
                      <p className="text-2xl font-bold">₹{(receivables.buckets?.[bucket.key] || 0).toLocaleString()}</p>
                    </div>
                    <p className="text-sm text-gray-600">{bucket.label}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Staff Workload */}
        <TabsContent value="staff">
          <Card>
            <CardHeader>
              <CardTitle>Staff Workload & Productivity</CardTitle>
              <CardDescription>Task distribution and performance metrics</CardDescription>
            </CardHeader>
            <CardContent>
              {staffProductivity?.staff_productivity?.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p>No staff members found. Add staff to track workload.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {staffProductivity?.staff_productivity?.map((staff, idx) => (
                    <div key={idx} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h4 className="font-medium">{staff.staff_name}</h4>
                          <p className="text-sm text-gray-500">{staff.role}</p>
                        </div>
                        <Badge 
                          className={
                            staff.performance_status === 'excellent' ? 'bg-green-500' :
                            staff.performance_status === 'good' ? 'bg-blue-500' :
                            staff.performance_status === 'average' ? 'bg-yellow-500' :
                            'bg-red-500'
                          }
                        >
                          {staff.efficiency_score}% Efficiency
                        </Badge>
                      </div>
                      <div className="grid grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">Completed</p>
                          <p className="font-medium text-green-600">{staff.tasks_completed}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Pending</p>
                          <p className="font-medium text-orange-600">{staff.tasks_pending}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Overdue</p>
                          <p className="font-medium text-red-600">{staff.tasks_overdue}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Avg Turnaround</p>
                          <p className="font-medium">{staff.avg_turnaround_days} days</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Client Health */}
        <TabsContent value="health">
          <Card>
            <CardHeader>
              <CardTitle>Client Health Scores</CardTitle>
              <CardDescription>Prioritize clients based on payment, compliance, and revenue contribution</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Summary */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-3xl font-bold">{clientHealth?.summary?.total_clients || 0}</p>
                  <p className="text-sm text-gray-500">Total Clients</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-3xl font-bold text-green-600">{clientHealth?.summary?.green_clients || 0}</p>
                  <p className="text-sm text-gray-500">Healthy (Green)</p>
                </div>
                <div className="text-center p-4 bg-yellow-50 rounded-lg">
                  <p className="text-3xl font-bold text-yellow-600">{clientHealth?.summary?.yellow_clients || 0}</p>
                  <p className="text-sm text-gray-500">At Risk (Yellow)</p>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <p className="text-3xl font-bold text-red-600">{clientHealth?.summary?.red_clients || 0}</p>
                  <p className="text-sm text-gray-500">Critical (Red)</p>
                </div>
              </div>

              {/* Client List */}
              <div className="space-y-3">
                {clientHealth?.client_health_scores?.map((client, idx) => (
                  <div key={idx} className="border rounded-lg p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h4 className="font-medium">{client.client_name}</h4>
                        <p className="text-sm text-gray-500">{client.gstin || 'No GSTIN'}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-2xl font-bold">{client.overall_score}</span>
                        <div className={`w-4 h-4 rounded-full ${
                          client.status === 'green' ? 'bg-green-500' :
                          client.status === 'yellow' ? 'bg-yellow-500' :
                          'bg-red-500'
                        }`}></div>
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-4 text-sm">
                      <div>
                        <div className="flex items-center gap-1 text-gray-500">
                          <IndianRupee className="w-3 h-3" />
                          Payment
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="flex-1 bg-gray-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${client.payment_score >= 70 ? 'bg-green-500' : client.payment_score >= 45 ? 'bg-yellow-500' : 'bg-red-500'}`}
                              style={{ width: `${client.payment_score}%` }}
                            ></div>
                          </div>
                          <span className="text-xs">{client.payment_score}</span>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-1 text-gray-500">
                          <CheckCircle className="w-3 h-3" />
                          Documents
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="flex-1 bg-gray-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${client.document_score >= 70 ? 'bg-green-500' : client.document_score >= 45 ? 'bg-yellow-500' : 'bg-red-500'}`}
                              style={{ width: `${client.document_score}%` }}
                            ></div>
                          </div>
                          <span className="text-xs">{client.document_score}</span>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-1 text-gray-500">
                          <TrendingUp className="w-3 h-3" />
                          Revenue
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="flex-1 bg-gray-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${client.revenue_score >= 70 ? 'bg-green-500' : client.revenue_score >= 45 ? 'bg-yellow-500' : 'bg-red-500'}`}
                              style={{ width: `${client.revenue_score}%` }}
                            ></div>
                          </div>
                          <span className="text-xs">{client.revenue_score}</span>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-1 text-gray-500">
                          <Calendar className="w-3 h-3" />
                          Compliance
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="flex-1 bg-gray-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${client.compliance_score >= 70 ? 'bg-green-500' : client.compliance_score >= 45 ? 'bg-yellow-500' : 'bg-red-500'}`}
                              style={{ width: `${client.compliance_score}%` }}
                            ></div>
                          </div>
                          <span className="text-xs">{client.compliance_score}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdvancedDashboard;
